import {isRefractoryDeleted} from '@domain/refractory-product-catalog'
import {RoleBasedPermission} from '@hconnect/apiclient'
import {useUpmPlantContext} from '@hconnect/upm'
import {Button, H1, H3, DashboardCard, Table, Pagination, Loading} from '@hm/fundamental'
import {usePlantPermission} from '@hproduce/permissions'
import {mdiFilterCogOutline, mdiUploadOutline} from '@mdi/js'
import {useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {getAdvancedFilterCount} from '../components/filters/RefractoryAdvancedFiltersModal'
import {
  EMPTY_REFRACTORY_CATALOG_FILTERS,
  RefractoryMaterialsFilters,
  type RefractoryCatalogFilters
} from '../components/filters/RefractoryMaterialsFilters'
import {RefraCatalogUploadModal} from '../components/upload/RefraCatalogUploadModal'
import {FEATURE_NAMESPACE} from '../consts'
import {
  useRefraCatalogConfig,
  useRefraCatalogData,
  useRefractoryCustomizationsQuery
} from '../hooks'
import {RefraModalProvider, useRefraModal} from '../providers'

export const RefractoryMaterialsPage: React.FC = () => (
  <RefraModalProvider>
    <RefractoryMaterials />
  </RefraModalProvider>
)

const RefractoryMaterials: React.FC = () => {
  const {t} = useTranslation(FEATURE_NAMESPACE)

  const upmPlant = useUpmPlantContext()
  const canUpload = usePlantPermission(RoleBasedPermission.HproduceRoleCCCSupporter)

  const [appliedFilters, setAppliedFilters] = useState(EMPTY_REFRACTORY_CATALOG_FILTERS)
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false)
  const advancedFilterCount = getAdvancedFilterCount(appliedFilters)
  const [page, setPage] = useState(0)
  const [limit, setLimit] = useState(20)

  const {
    data: refractoriesData,
    isLoading: isRefractoriesLoading,
    isError: isRefractoriesError
  } = useRefractoryCustomizationsQuery({
    plantUpmId: upmPlant.upmId,
    page,
    limit,
    name: appliedFilters.name || undefined,
    manufacturerIds: appliedFilters.manufacturerIds.map(Number),
    classificationIds: appliedFilters.classificationIds.map(Number),
    classificationGroupIds: appliedFilters.classificationGroupIds.map(Number),
    bondingIds: appliedFilters.bondingIds.map(Number),
    placingMethods: appliedFilters.placingMethods,
    chemicalData: appliedFilters.chemicalData,
    csRatio: appliedFilters.csRatio,
    physicalData: appliedFilters.physicalData
  })

  const {modal, openModal} = useRefraModal()
  const visibleRefractories = useMemo(
    () =>
      refractoriesData?.refractories.filter((refractory) => !isRefractoryDeleted(refractory)) ?? [],
    [refractoriesData?.refractories]
  )

  const refraCatalogConfig = useRefraCatalogConfig()
  const refraCatalogData = useRefraCatalogData({
    refractories: visibleRefractories
  })

  const onChange: (page: number, pageSize: number) => void = (page, pageSize) => {
    setPage(page)
    setLimit(pageSize)
  }

  const onApplyFilters = (filters: RefractoryCatalogFilters) => {
    setAppliedFilters(filters)
    setPage(0)
  }

  const onResetFilters = () => {
    setAppliedFilters(EMPTY_REFRACTORY_CATALOG_FILTERS)
    setPage(0)
  }

  if (!upmPlant) {
    return (
      <div className="h-screen flex items-center justify-center">
        <Loading size="xlarge" data-cy="refra-catalog-loader" />
      </div>
    )
  }

  return (
    <div className="px-6">
      <H1 className="my-6 ml-6">{t('refraCatalog.title')}</H1>
      <DashboardCard data-cy="refra-catalog-card">
        <div className="flex flex-col gap-6">
          <div
            className="flex flex-wrap items-center justify-between gap-x-8 gap-y-3"
            data-test-id="refra_catalog_title_toolbar"
          >
            <H3>{t('refraCatalog.refractories')}</H3>
            <div className="flex flex-wrap items-center justify-end gap-3">
              <Button
                type="button"
                variant="tertiary"
                size="large"
                icon={mdiFilterCogOutline}
                iconSize="regular"
                className="gap-x-2"
                onClick={() => setIsAdvancedOpen(true)}
                data-cy="refra-catalog-advanced-filters"
              >
                {t('refraCatalog.filters.advanced')}
                {advancedFilterCount > 0 && ` (${advancedFilterCount})`}
              </Button>
              {canUpload && (
                <Button
                  type="button"
                  variant="tertiary"
                  size="large"
                  icon={mdiUploadOutline}
                  iconSize="regular"
                  className="gap-x-2"
                  onClick={() => openModal(<RefraCatalogUploadModal />)}
                  data-cy="refra-catalog-upload-button"
                >
                  {t('refraCatalog.upload.open')}
                </Button>
              )}
            </div>
          </div>
          <RefractoryMaterialsFilters
            appliedFilters={appliedFilters}
            onApply={onApplyFilters}
            onReset={onResetFilters}
            isAdvancedOpen={isAdvancedOpen}
            onCloseAdvancedFilters={() => setIsAdvancedOpen(false)}
          />
          {isRefractoriesLoading && !refractoriesData ? (
            <div
              className="flex h-64 items-center justify-center"
              role="status"
              aria-label={t('refraCatalog.loading')}
              data-cy="refra-catalog-loader"
            >
              <Loading size="large" />
            </div>
          ) : isRefractoriesError ? (
            <>
              <p className="py-4 text-center" role="alert" data-cy="refra-catalog-error">
                {t('error.refractoryCustomizationsQuery')}
              </p>
              {refractoriesData && visibleRefractories.length > 0 && (
                <Table
                  config={refraCatalogConfig}
                  data={refraCatalogData}
                  breakpoint="sm"
                  data-cy="refra-catalog-table"
                />
              )}
            </>
          ) : refractoriesData && visibleRefractories.length === 0 ? (
            <p className="py-12 text-center" role="status" data-cy="refra-catalog-no-results">
              {t('refraCatalog.noResults')}
            </p>
          ) : (
            <Table
              config={refraCatalogConfig}
              data={refraCatalogData}
              breakpoint="sm"
              data-cy="refra-catalog-table"
            />
          )}
          <Pagination
            key={`${page}-${limit}`}
            label={t('refraCatalog.paginationPlaceholder')}
            totalItems={refractoriesData?.totalCount ?? 0}
            defaultPage={page}
            defaultPageSize={limit}
            onChange={onChange}
            data-cy="refra-catalog-pagination"
          />

          {modal}
        </div>
      </DashboardCard>
    </div>
  )
}
